import type { AdapterOption, ScannerInfo } from '@/api/types'
import { scannerOptionsFor } from '@/lib/scanner-options'

interface ScannerOptionsEditorProps {
  scanner: ScannerInfo
  /**
   * The source type being configured. Options outside their declared scope
   * are hidden rather than disabled: the API refuses them, so a greyed-out
   * field would read as something the user could switch on.
   */
  sourceType: string
  value: Record<string, unknown>
  onChange: (v: Record<string, unknown>) => void
}

function OptionField({
  opt,
  value,
  onChange,
}: {
  opt: AdapterOption
  value: unknown
  onChange: (v: unknown) => void
}) {
  const label = (
    <div className="min-w-0">
      <p className="text-xs font-medium text-text-light dark:text-text-dark">
        {opt.key}
        {opt.required && <span className="text-error ml-1">*</span>}
      </p>
      <p className="text-xs text-subtle-light dark:text-subtle-dark leading-snug mt-0.5">
        {opt.description}
      </p>
    </div>
  )

  if (opt.type === 'bool') {
    const on = !!value
    return (
      <div className="flex items-start justify-between gap-4">
        {label}
        <button
          type="button"
          role="switch"
          aria-checked={on}
          onClick={() => onChange(!on)}
          className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50 ${
            on ? 'bg-primary' : 'bg-border-light dark:bg-border-dark'
          }`}
        >
          <span
            className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
              on ? 'translate-x-4' : 'translate-x-0.5'
            }`}
          />
        </button>
      </div>
    )
  }

  if (opt.type === 'select' && opt.choices) {
    return (
      <div className="flex items-start justify-between gap-4">
        {label}
        <select
          value={(value as string) || (opt.default !== undefined ? String(opt.default) : '')}
          onChange={(e) => onChange(e.target.value || undefined)}
          className="shrink-0 pl-2 pr-6 py-1 text-xs rounded-lg bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          {opt.choices.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>
    )
  }

  // int / string — full-width input below label
  return (
    <div>
      {label}
      <input
        type={opt.type === 'int' ? 'number' : 'text'}
        value={(value as string | number) ?? ''}
        onChange={(e) =>
          onChange(
            opt.type === 'int'
              ? e.target.value === ''
                ? undefined
                : Number(e.target.value)
              : e.target.value || undefined
          )
        }
        placeholder={opt.default !== undefined ? String(opt.default) : undefined}
        className="mt-1.5 w-full px-2.5 py-1.5 text-xs rounded-lg bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/50"
      />
    </div>
  )
}

export default function ScannerOptionsEditor({
  scanner,
  sourceType,
  value,
  onChange,
}: ScannerOptionsEditorProps) {
  const options = scannerOptionsFor(scanner, sourceType)
  if (options.length === 0) return null

  const setField = (key: string, v: unknown) => {
    const next = { ...value }
    if (v === undefined) {
      delete next[key]
    } else {
      next[key] = v
    }
    onChange(next)
  }

  return (
    <div className="space-y-3">
      {options.map((opt) => (
        <OptionField
          key={opt.key}
          opt={opt}
          value={value[opt.key]}
          onChange={(v) => setField(opt.key, v)}
        />
      ))}
    </div>
  )
}
